import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../src/environments/environment';
import { DOCUMENT } from '@angular/common';
import { SharedCookieService } from './shared-cookie.service';
import { EmbedService } from './embed.service';

@Injectable({
  providedIn: 'root'
})
export class SwitchLanguageService {

  defaultLanguage = environment.defaultLanguage;
  supportedLanguages = environment.supportedLanguages;
  currentLanguage = 'en';

  /**
   * Name of the cookie that carries the selected UI language. Shared verbatim with Keycloak (via
   * [[SharedCookieService]]): the Keycloak login theme reads it to render the login page in the app's
   * language and writes it back after an on-page language change — so the app and the login page stay in
   * sync in both directions through the cookie alone, with no `kc_locale` round-trip or post-login reload.
   */
  private static readonly LANGUAGE_COOKIE = 'current_language';

  /** Whether the user had an explicitly stored language when the app booted (vs. falling back to the
   * default). Gates the shared-domain migration so we never invent a preference for brand-new users. */
  private readonly hadStoredLanguage: boolean;

  constructor(private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private sharedCookie: SharedCookieService,
    private embedService: EmbedService) {
    // Validate/normalize on read: the cookie is shared and co-written by an EXTERNAL system (the Keycloak
    // theme), so a region-qualified ('ar-EG') or unsupported value can arrive. Treat only a supported,
    // normalized value as a real stored preference.
    // While embedded, the language the Edara Portal passes on the frame URL outranks the cookie: the
    // portal's header is the only language switcher the user can see, so its choice must win — and it
    // must be resolved here, before the first paint, rather than corrected after one.
    const portalLanguage = this.normalizeLanguage(this.embedService.param('lang'));
    const stored = portalLanguage ?? this.normalizeLanguage(this.sharedCookie.get(SwitchLanguageService.LANGUAGE_COOKIE));
    this.hadStoredLanguage = stored != null;
    if (stored) {
      this.currentLanguage = stored;
    }

    //this.translateService.onLangChange.subscribe((event: LangChangeEvent) => this.onLanguageChanged(event.lang));
  }

  load() {
    return new Promise<any>((resolve: any) => {
      console.log('Loading language...');
      // Migrate an EXISTING preference onto the shared domain so Keycloak sees it even for users who
      // never re-pick a language after this rolls out (idempotent). Guarded on hadStoredLanguage so a
      // brand-new user (no cookie) is left to Keycloak's realm default instead of being pinned to ours.
      if (this.hadStoredLanguage) {
        this.writeLanguageCookie(this.currentLanguage);
      }
      this.translateService.setDefaultLang('en');
      this.translateService.use(this.currentLanguage).subscribe({
        next: () => {
          this.setDirection(this.currentLanguage);
          console.info(`Successfully initialized '${this.currentLanguage}' language.'`);
        },
        error: () => {
          console.error(`Problem with '${this.currentLanguage}' language initialization.'`);
        },
        complete: () => {
          resolve(null);
        }
      });
    });
  }

  useCurrentLanguage() {
    this.translateService.use(this.currentLanguage);
    this.setDirection(this.currentLanguage)
  }

  useLanguage(language: string) {
    //this.translateService.use(language);
    this.currentLanguage = language;
    this.writeLanguageCookie(language);
    location.reload();
  }

  /**
   * Switches language in place, without the full reload [[useLanguage]] does.
   *
   * Used for the language the Edara Portal pushes into the embedded frame: a reload there is not
   * a reload of a page but a cold restart of the whole product — IndexedDB reopened, SignalR
   * reconnected, bundle re-parsed, any half-filled form lost — which is exactly what embedding
   * over a live `postMessage` channel exists to avoid.
   *
   * An unsupported value is ignored rather than applied, since this comes from another window.
   */
  applyLanguage(language: string) {
    const normalized = this.normalizeLanguage(language);
    if (!normalized || normalized === this.currentLanguage) {
      return;
    }
    this.currentLanguage = normalized;
    this.writeLanguageCookie(normalized);
    this.translateService.use(normalized);
    this.setDirection(normalized);
  }

  /** Write the selected language to the shared cookie (never an unsupported value). */
  private writeLanguageCookie(language: string): void {
    if (!this.supportedLanguages.includes(language)) {
      return;
    }
    this.sharedCookie.set(SwitchLanguageService.LANGUAGE_COOKIE, language);
  }

  /**
   * Normalize a raw locale to a supported app language (primary subtag, lower-cased), or null when it
   * is absent/unsupported, so Keycloak-sourced values ('ar-EG', an unexpected realm locale, a tampered
   * value) are all handled safely on read.
   */
  private normalizeLanguage(raw: string | null | undefined): string | null {
    if (!raw) {
      return null;
    }
    const lang = String(raw).split(/[-_]/)[0].toLowerCase();
    return this.supportedLanguages.includes(lang) ? lang : null;
  }

  private setDirection(lang: string) {
    this.document.documentElement.lang = lang;
    if (lang === environment.defaultLanguage) {
      this.document.documentElement.dir = 'rtl';
    } else {
      this.document.documentElement.dir = 'ltr';
    }
  }


}
